import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import {
    User,
    signInWithEmailAndPassword,
    signOut as firebaseSignOut,
    onAuthStateChanged,
    createUserWithEmailAndPassword
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { initializeAdminUser, AppUser } from '../services/users';

interface AuthContextType {
    user: User | null;
    appUser: AppUser | null;
    isSuperAdmin: boolean;
    isContractorAdmin: boolean;
    loading: boolean;
    signIn: (email: string, password: string) => Promise<void>;
    signUp: (email: string, password: string) => Promise<void>;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [appUser, setAppUser] = useState<AppUser | null>(null);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [isContractorAdmin, setIsContractorAdmin] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let unsubscribeProfile: () => void = () => { };

        const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
            setUser(firebaseUser);

            if (firebaseUser) {
                try {
                    // Initialize user profile (creates if doesn't exist)
                    await initializeAdminUser(firebaseUser.uid, firebaseUser.email || '');

                    // Subscribe to user profile changes
                    unsubscribeProfile = onSnapshot(doc(db, 'users', firebaseUser.uid), (doc) => {
                        if (doc.exists()) {
                            const data = doc.data();
                            const profile: AppUser = {
                                uid: doc.id,
                                ...data
                            } as AppUser;

                            setAppUser(profile);
                            setIsSuperAdmin(profile.role === 'super_admin');
                            setIsContractorAdmin(profile.role === 'admin' || profile.role === 'super_admin');
                        }
                        setLoading(false);
                    }, (error) => {
                        console.error("Error subscribing to user profile:", error);
                        setLoading(false);
                    });

                } catch (err) {
                    console.error('Error initializing user:', err);
                    setLoading(false);
                }
            } else {
                setAppUser(null);
                setIsSuperAdmin(false);
                setIsContractorAdmin(false);
                setLoading(false);
                if (unsubscribeProfile) unsubscribeProfile();
            }
        });

        return () => {
            unsubscribeAuth();
            if (unsubscribeProfile) unsubscribeProfile();
        };
    }, []);

    const signIn = async (email: string, password: string) => {
        await signInWithEmailAndPassword(auth, email, password);
    };

    const signUp = async (email: string, password: string) => {
        await createUserWithEmailAndPassword(auth, email, password);
    };

    const signOut = async () => {
        await firebaseSignOut(auth);
        setAppUser(null);
        setIsSuperAdmin(false);
    };

    return (
        <AuthContext.Provider value={{ user, appUser, isSuperAdmin, isContractorAdmin, loading, signIn, signUp, signOut }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
